import { forwardRef, type ReactNode } from 'react'
import {
  Checkbox as MuiCheckbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  type CheckboxProps as MuiCheckboxProps,
  type FormControlLabelProps,
  type FormControlProps,
} from '@mui/material'

export type CheckboxShape = 'default' | 'rounded'

export type CheckboxProps = Omit<MuiCheckboxProps, 'children'> & {
  error?: boolean
  formControlLabelProps?: Omit<FormControlLabelProps, 'control' | 'disabled' | 'label' | 'required'>
  formControlProps?: Omit<FormControlProps, 'disabled' | 'error' | 'required'>
  helperText?: ReactNode
  label?: ReactNode
  shape?: CheckboxShape
}

type CheckboxIndicatorProps = {
  shape: CheckboxShape
  state: 'unchecked' | 'checked' | 'indeterminate'
}

function CheckboxIndicator({ shape, state }: CheckboxIndicatorProps) {
  return (
    <span aria-hidden className="CodesignCheckboxIndicator" data-shape={shape} data-state={state}>
      {state === 'checked' ? (
        <svg fill="none" viewBox="0 0 20 20">
          <path d="m6 10 3 3 6-6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" />
        </svg>
      ) : null}
      {state === 'indeterminate' ? (
        <svg fill="none" viewBox="0 0 20 20">
          <path d="M6 10h8" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" />
        </svg>
      ) : null}
    </span>
  )
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  {
    checkedIcon,
    error = false,
    formControlLabelProps,
    formControlProps,
    helperText,
    icon,
    indeterminateIcon,
    label,
    shape = 'default',
    slotProps,
    ...props
  },
  ref,
) {
  const inputSlotProps = slotProps?.input
  const indeterminateAriaState = props.indeterminate ? 'mixed' : undefined

  const control = (
    <MuiCheckbox
      {...props}
      checkedIcon={checkedIcon ?? <CheckboxIndicator shape={shape} state="checked" />}
      icon={icon ?? <CheckboxIndicator shape={shape} state="unchecked" />}
      indeterminateIcon={indeterminateIcon ?? <CheckboxIndicator shape={shape} state="indeterminate" />}
      slotProps={{
        ...slotProps,
        input:
          typeof inputSlotProps === 'function'
            ? ownerState => {
                const resolved = inputSlotProps(ownerState)

                return {
                  'aria-checked': indeterminateAriaState,
                  ...resolved,
                  ref,
                }
              }
            : {
                'aria-checked': indeterminateAriaState,
                ...(slotProps?.input ?? {}),
                ref,
              },
      }}
    />
  )

  if (!label && !helperText) {
    return control
  }

  return (
    <FormControl {...formControlProps} disabled={props.disabled} error={error} required={props.required}>
      {label ? <FormControlLabel {...formControlLabelProps} control={control} label={label} /> : control}
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </FormControl>
  )
})
